import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export type MobileNavSidebarVariant =
  | 'application'
  | 'findings'
  | 'compliance'
  | 'none';

interface MobileNavContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  sidebarVariant: MobileNavSidebarVariant;
  setSidebarVariant: (variant: MobileNavSidebarVariant) => void;
}

const MobileNavContext = createContext<MobileNavContextValue | null>(null);

export const MobileNavProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [sidebarVariant, setSidebarVariant] =
    useState<MobileNavSidebarVariant>('none');

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  const value = useMemo(
    () => ({
      isOpen,
      open,
      close,
      toggle,
      sidebarVariant,
      setSidebarVariant,
    }),
    [isOpen, open, close, toggle, sidebarVariant],
  );

  return (
    <MobileNavContext.Provider value={value}>
      {children}
    </MobileNavContext.Provider>
  );
};

export const useMobileNav = (): MobileNavContextValue => {
  const ctx = useContext(MobileNavContext);
  if (!ctx) {
    throw new Error('useMobileNav must be used within a MobileNavProvider');
  }
  return ctx;
};
